/**
 * UpgradeRequiredModal
 *
 * Reusable modal popup shown when a coach tries to use a feature that
 * isn't included in their current subscription tier. First consumer is
 * the listings-limit gate in DashboardListingWrapper / DashboardAddListing
 * Wrapper. Same component will be reused as we gate more features
 * (file uploads, programs, reporting, enterprise opportunities, etc.).
 *
 * Visual language mirrors SubscriptionMigrationBanner — forest-green to
 * deep-purple gradient on a darkened backdrop, gold accent, soft slide-in
 * animation. Pure inline styles so it can drop into any container
 * without depending on a stylesheet import order.
 *
 * Accessibility
 *   - role="dialog", aria-modal="true", aria-labelledby on the heading
 *   - Backdrop click closes (with onClick.stopPropagation on the card)
 *   - Escape key handler attaches/detaches with the open state
 *   - X close button + secondary "Not now" button as escape hatches
 *
 * Props
 *   isOpen            (boolean) controls visibility
 *   onClose           (() => void) called on backdrop click, Escape, X,
 *                                  and "Not now"
 *   onUpgrade         (() => void) called when the primary CTA is
 *                                  clicked — parent should open
 *                                  Chargebee checkout for the target plan
 *   featureName       (string) what the coach was trying to do
 *                              e.g. "create another active listing"
 *   currentTierName   (string) e.g. "Network Member"
 *   requiredTierName  (string) e.g. "Verified Coach"
 *   limit             (number, optional) used in the headline copy when
 *                                        present, e.g. "3 of 3 listings used"
 *   used              (number, optional) paired with `limit`
 *   reason            (string, optional) one of the precheck reason
 *                                        codes — toggles secondary copy:
 *                                        'listing_limit_reached' (default)
 *                                        'no_subscription'
 *                                        'subscription_expired'
 */

import React, { useEffect } from 'react';

const UpgradeRequiredModal = ({
  isOpen,
  onClose,
  onUpgrade,
  featureName,
  currentTierName,
  requiredTierName,
  limit,
  used,
  reason = 'listing_limit_reached',
}) => {
  // Escape key closes when open
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = e => {
      if (e.key === 'Escape') {
        onClose && onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = () => {
    onClose && onClose();
  };
  const handleCardClick = e => e.stopPropagation();

  // ── Copy variants ────────────────────────────────────
  // Reason-specific headline + body; otherwise default to the most
  // common case (limit reached).
  let headline;
  let body;
  let primaryLabel = `Upgrade to ${requiredTierName || 'Verified Coach'}`;
  if (reason === 'no_subscription') {
    headline = 'A subscription is required';
    body = `To ${featureName || 'use this feature'}, please activate a Viveka coach subscription. You'll get a 7-day free trial — no charge until day 8.`;
    primaryLabel = 'Choose a plan';
  } else if (reason === 'subscription_expired') {
    headline = 'Your subscription has expired';
    body = `Your ${currentTierName || 'coach'} plan has lapsed. Reactivate to ${featureName || 'continue'} without losing your data.`;
    primaryLabel = 'Reactivate subscription';
  } else {
    // listing_limit_reached (default)
    const countCopy =
      typeof used === 'number' && typeof limit === 'number'
        ? ` You're using ${used} of ${limit} listings on your current plan.`
        : '';
    headline = `Upgrade to ${requiredTierName || 'Verified Coach'} for more`;
    body = `${featureName || 'This action'} isn't available on your ${currentTierName || 'current'} plan.${countCopy} Upgrade to ${requiredTierName || 'Verified Coach'} to unlock the full feature set — verification badge, priority search, 10 active listings, enterprise opportunities, and more.`;
  }

  return (
    <>
      <style>{`
        @keyframes upgradeModalFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes upgradeModalSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes upgradeModalPulseGlow {
          0%, 100% {
            box-shadow:
              0 4px 20px rgba(251, 191, 36, 0.45),
              0 0 0 0 rgba(251, 191, 36, 0.4);
          }
          50% {
            box-shadow:
              0 6px 28px rgba(251, 191, 36, 0.6),
              0 0 0 8px rgba(251, 191, 36, 0);
          }
        }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-modal-title"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          animation: 'upgradeModalFadeIn 0.25s ease-out',
        }}
      >
        {/* Card */}
        <div
          onClick={handleCardClick}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '520px',
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
            padding: '32px',
            borderRadius: '20px',
            background:
              'radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.32) 0%, rgba(139, 92, 246, 0) 55%), ' +
              'linear-gradient(135deg, #064E3B 0%, #0F3F4A 45%, #1E1040 100%)',
            border: '1px solid rgba(251, 191, 36, 0.35)',
            boxShadow: '0 30px 80px rgba(15, 23, 42, 0.55)',
            color: '#FFFFFF',
            animation: 'upgradeModalSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Close (X) button */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '14px',
              right: '14px',
              width: '34px',
              height: '34px',
              borderRadius: '50%',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              background: 'rgba(255, 255, 255, 0.08)',
              color: '#FFFFFF',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              lineHeight: 1,
            }}
          >
            ×
          </button>

          {/* Eyebrow */}
          <div
            style={{
              display: 'inline-block',
              padding: '6px 14px',
              marginBottom: '18px',
              borderRadius: '999px',
              background: 'rgba(251, 191, 36, 0.18)',
              border: '1px solid rgba(251, 191, 36, 0.45)',
              color: '#FBBF24',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '1.4px',
              textTransform: 'uppercase',
            }}
          >
            Upgrade required
          </div>

          {/* Heading */}
          <h2
            id="upgrade-modal-title"
            style={{
              margin: '0 0 12px',
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: 1.25,
              color: '#FFFFFF',
            }}
          >
            {headline}
          </h2>

          {/* Body */}
          <p
            style={{
              margin: '0 0 24px',
              fontSize: '15px',
              lineHeight: 1.55,
              color: 'rgba(255, 255, 255, 0.86)',
            }}
          >
            {body}
          </p>

          {/* CTAs */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={onUpgrade}
              style={{
                appearance: 'none',
                cursor: 'pointer',
                padding: '14px 22px',
                borderRadius: '12px',
                border: 'none',
                background: '#FBBF24',
                color: '#0F172A',
                fontWeight: 700,
                fontSize: '15px',
                letterSpacing: '0.2px',
                animation: 'upgradeModalPulseGlow 2.4s ease-in-out infinite',
              }}
            >
              {primaryLabel}
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                appearance: 'none',
                cursor: 'pointer',
                padding: '14px 18px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                background: 'transparent',
                color: '#FFFFFF',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpgradeRequiredModal;
